import type { App, ComputedRef, Ref } from 'vue'
import { computed, inject, ref, toRef, watch } from 'vue'
import { type AppTranslator } from '@/i18n'
import { type RegionPlugin } from '@/plugins/region'
import type {
    AffirmFinanceOptionsFragment,
    CatalogueEngagementRingFilterInput,
    CatalogueEngagementRingInputFragment,
    CatalogueEngagementRingSignatureStoneInput,
    CurrencyInput,
    GemsApi_StoneCategory,
    KlarnaFinanceOptionsFragment,
    MoneyFragment,
    MonolithImageSizeFragment,
    RbFragment,
    RingBuilderCentreStoneFragmentFragment,
    RingBuilderCentreStoneFragmentInput,
    RingBuilderFragmentFragment,
    RingBuilderMountFragmentFragment,
    RingBuilderSetMutation,
    RingBuilderSetMutationVariables,
    TaxSnippetFragment,
} from '@/graphql/api'
import type {
    RingBuilderCentreStoneShapeConstraint,
    RingBuilderRingTypes,
    RingBuilderStoneTypes,
} from '@/domain/product/ring/ring-builder'
import {
    getStoneCategory,
    getStoneColorAndClarity,
    getStoneShape,
    getStoneWeight,
} from '@/domain/product/ring/ring-builder'
import type { GraphQLTypeName } from '@/helpers'
import { assertNever } from '@/helpers'
import { getUri } from '@/helpers/getCurrentUri'
import {
    gemsApiStoneToInput,
    getCatalogueEngagementRingFilterInput,
    nonSkuLabGemstoneToFilterInput,
    nonSkuLabGrownDiamondToFilterInput,
} from '@/domain/product/ring/engagement'
import { type Routes } from '@/router/routes'
import type { NonSkuParams } from '@/views/product/engagement-ring/composables/nonSkuParams'
import {
    ROUTE_ENGAGEMENT_RING,
    ROUTE_ENGAGEMENT_RING_GEMSTONE,
    ROUTE_ENGAGEMENT_RING_LAB_GROWN,
    ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS,
    ROUTE_ENGAGEMENT_RINGS,
    ROUTE_FANCY_DIAMOND_SEARCH,
    ROUTE_GEMSTONE_SEARCH,
    ROUTE_LAB_GROWN_DIAMOND_SEARCH,
    ROUTE_NATURAL_DIAMOND_SEARCH,
    ROUTE_NON_SKU_GEMSTONE_SEARCH,
    ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH,
} from '@/router'
import { type RouterNavigationPlugin } from '@/plugins/released-urls/useRouterNavigation'
import {
    type RingRecommendedSizeOrSizeValue,
    useRingSize,
} from '@/domain/product/ring/useRingSize'
import { toCertificate } from '@/domain/stone/certificate'
import { cloneDeep } from '@/helpers/clone-deep.ts'
import type { MoneyFormatter } from '@/plugins/money-formatter.ts'
import { useRouter } from 'vue-router'
import type { SimpleMutationResponse } from '@/helpers/mutation-adapter.ts'
import {
    type RingBuilderCookieStore,
    useRingBuilderStore,
} from '@/plugins/ring-builder-store.ts'

export type SignatureStone =
    | CatalogueEngagementRingSignatureStoneInput
    | undefined
export type RingSize = RingRecommendedSizeOrSizeValue | null
export type StoneUri = string | null

const key = 'app.plugin.ring_builder' as const

export function provideRingBuilderDataStore(app: App): RingBuilderDataStore {
    const store = createRingBuilderDataStore()

    app.provide(key, store)

    return store
}

export function createRingBuilderDataStore(): RingBuilderDataStore {
    const state: Ref<RbFragment | null | undefined> = ref(null)

    return {
        state: computed(() => state.value),
        setState(newState: RbFragment | null | undefined) {
            state.value = cloneDeep(newState)
        },
    }
}

export interface RingBuilderDataStore {
    state: ComputedRef<RbFragment | null | undefined>
    setState(state: RbFragment | null | undefined): void
}

export function useRingBuilderDataStore(): RingBuilderDataStore {
    return inject(key) as RingBuilderDataStore
}

type AddRingTypes =
    | CatalogueEngagementRingInputFragment
    | CatalogueEngagementRingFilterInput
type AddStoneTypes = RingBuilderStoneTypes | NonSkuParams

export interface RingBuilder {
    // Main items
    ring: Readonly<Ref<RingBuilderRingTypes | null>>
    stone: ComputedRef<RingBuilderStoneTypes | null>

    // Cookie-persisted store
    stoneUri: Readonly<Ref<StoneUri>>
    signatureStone: Ref<SignatureStone>

    lastAdded: Readonly<Ref<LastAddedTypename | null>>

    fetchingRingBuilderSetMutation: Readonly<Ref<boolean>>
    isValid: Readonly<Ref<boolean>>

    // Actions
    addRing(
        r: AddRingTypes,
        signatureStone?: CatalogueEngagementRingSignatureStoneInput | undefined
    ): Promise<RingBuilderRingTypes | null>
    removeRing(): Promise<void>
    addStone(r: AddStoneTypes): Promise<RingBuilderStoneTypes | null>
    removeStone(): Promise<void>
    addRingAndStone(
        r: AddRingTypes,
        s: AddStoneTypes,
        signatureStone?: CatalogueEngagementRingSignatureStoneInput | undefined
    ): Promise<[RingBuilderRingTypes | null, RingBuilderStoneTypes | null]>

    // Data Convenience
    productId: Readonly<Ref<string>>
    image: Readonly<
        Ref<
            | {
                  small_x2: MonolithImageSizeFragment
                  large_x2: MonolithImageSizeFragment
              }
            | undefined
        >
    >
    imageFor3rdStep: Readonly<
        Ref<
            | {
                  small_x2: MonolithImageSizeFragment
                  large_x2: MonolithImageSizeFragment
              }
            | undefined
            | null
        >
    >
    mountFragment: Readonly<Ref<RingBuilderMountFragmentFragment | null>>
    centreStoneFragment: Readonly<
        Ref<RingBuilderCentreStoneFragmentFragment | null>
    >

    // Random Strings
    stoneShapeAndWeight: Readonly<Ref<string>>
    stoneDescription: Readonly<Ref<string>>

    // Finance Options
    affirmFinanceOptions: Readonly<
        Ref<AffirmFinanceOptionsFragment | undefined | null>
    >
    klarnaFinanceOptions: Readonly<
        Ref<KlarnaFinanceOptionsFragment | undefined | null>
    >

    // Prices
    // todo those two ???
    ringTotalSaleAmount: Readonly<Ref<number>>
    ringTotalSaleAmountFormatted: Readonly<Ref<string>>

    // todo these 4 total sales are redundant as they are
    salePrice: Readonly<Ref<TaxSnippetFragment | null | undefined>>
    totalSale: Readonly<Ref<MoneyFragment>>
    totalSaleAmount: Readonly<Ref<number>>
    totalSaleAmountFormatted: Readonly<Ref<string>>
}

type LastAddedTypename =
    | GraphQLTypeName<RingBuilderRingTypes>
    | GraphQLTypeName<RingBuilderStoneTypes>

const getMountFromPosition = (
    position: RingBuilderFragmentFragment | null
): RingBuilderMountFragmentFragment | null =>
    position?.__typename !== 'RingBuilderMountFragment' ? null : position

export const getMount = (
    rb: RbFragment | null | undefined
): RingBuilderMountFragmentFragment | null =>
    !rb
        ? null
        : getMountFromPosition(rb.position1) ||
          getMountFromPosition(rb.position2)

const getCentreStoneFromPosition = (
    position: RingBuilderFragmentFragment | null
): RingBuilderCentreStoneFragmentFragment | null =>
    position?.__typename !== 'RingBuilderCentreStoneFragment' ? null : position

export const getCentreStone = (
    rb: RbFragment | null | undefined
): RingBuilderCentreStoneFragmentFragment | null =>
    !rb
        ? null
        : getCentreStoneFromPosition(rb.position1) ||
          getCentreStoneFromPosition(rb.position2)

const ringBuilderKey = 'app.plugin.ringBuilder.real.todo.rename.others'

export function installRingBuilder(
    app: App,
    dependencies: RingBuilderDependencies
) {
    const ringBuilder = createRingBuilder(dependencies)

    app.provide(ringBuilderKey, ringBuilder)
}

export function useRingBuilder(): RingBuilder {
    return inject(ringBuilderKey) as RingBuilder
}

interface RingBuilderDependencies {
    translator: AppTranslator
    currency: ComputedRef<CurrencyInput>
    regionPlugin: RegionPlugin
    formatMoney: MoneyFormatter
    routes: Routes
    routerNavigation: RouterNavigationPlugin
    ringBuilderDataStore: RingBuilderDataStore
    ringBuilderCookieStore: RingBuilderCookieStore
    ringBuilderSetMutation: SimpleMutationResponse<
        RingBuilderSetMutation,
        RingBuilderSetMutationVariables
    >
}

function createRingBuilder(dependencies: RingBuilderDependencies): RingBuilder {
    const {
        translator,
        currency,
        regionPlugin,
        formatMoney,
        routes,
        routerNavigation,
        ringBuilderDataStore,
        ringBuilderCookieStore,
        ringBuilderSetMutation,
    } = dependencies

    const { locale } = translator
    const { regionCode } = regionPlugin

    const { state: ringBuilder } = ringBuilderDataStore
    const { stoneUri, signatureStone } = ringBuilderCookieStore

    const mountFragment = computed(() => getMount(ringBuilder.value))
    const centreStoneFragment = computed(() =>
        getCentreStone(ringBuilder.value)
    )

    const ring = toRef(() => mountFragment.value?.ring || null)

    const stone = computed(() => {
        const fragment = centreStoneFragment.value

        if (!fragment) {
            return null
        }

        return fragment.stone?.__typename === 'GemsApiStone'
            ? fragment.stone.stone
            : fragment.stone
    })

    // const {
    //     executeMutation: executeRingBuilderSetMutation,
    //     fetching: fetchingRingBuilderSetMutation,
    // } = useRingBuilderSetMutation()
    const {
        executeMutation: executeRingBuilderSetMutation,
        fetching: fetchingRingBuilderSetMutation,
    } = ringBuilderSetMutation

    const mountTotalSalePrice = toRef(
        () =>
            mountFragment.value?.totalSalePrice || {
                __typename: 'Money',
                currency: 'GBP',
                amount: '0',
            }
    )

    const centreStoneTotalSalePrice = computed(
        () =>
            centreStoneFragment.value?.totalSalePrice ||
            ({
                __typename: 'Money',
                currency: 'GBP',
                amount: '0',
            } as const)
    )
    const stoneTotalSaleAmountFormatted = computed(() =>
        formatMoney(centreStoneTotalSalePrice.value)
    )

    const stoneShapeAndWeight = computed(
        () => `${getStoneShape(stone.value)} ${getStoneWeight(stone.value)}`
    )

    const stoneCertificate = toCertificate(
        computed(() => {
            if (stone.value?.__typename === 'GemsApi_Stone') {
                return stone.value
            }
            return null
        })
    )

    const stoneCertificateDescription = computed(() => {
        if (!stoneCertificate.value) {
            return ''
        }
        return `, ${stoneCertificate.value.lab} Cert`
    })

    const stoneDescription = computed(
        () =>
            `${getStoneCategory(stone.value)}, ${getStoneColorAndClarity(
                stone.value
            )}${stoneCertificateDescription.value}: ${stoneTotalSaleAmountFormatted.value}`
    )

    const { routerPush } = routerNavigation

    watch(stone, (newValue) => {
        if (newValue) {
            stoneUri.value = getUri()

            // const route = router.currentRoute
            //
            // // todo take this redirect the fuck out of here and into ER.vue
            // if (
            //     ring.value &&
            //     (route.value.name === ROUTE_ENGAGEMENT_RING_GEMSTONE ||
            //         route.value.name === ROUTE_ENGAGEMENT_RING_LAB_GROWN)
            // ) {
            //     routerPush(routes.ringBuilder.ring(ring.value, newValue))
            // }
        } else {
            stoneUri.value = null
        }
    })

    const addRing: RingBuilder['addRing'] = async (r, signatureStone) =>
        executeRingBuilderSetMutation({
            currency: currency.value,
            locale: locale.value,
            region: regionCode.value,
            input: {
                mount: {
                    engagementRing:
                        '__typename' in r
                            ? getCatalogueEngagementRingFilterInput(r)
                            : r,
                    signatureStone,
                },
            },
        }).then(() => {
            return ring.value
        })

    const removeRing: RingBuilder['removeRing'] = async () => {
        if (!ring.value) {
            return
        }

        routerPush(
            routes.engagementRings({
                shape: ring.value.centreStoneConstraints.shape.systemCode,
            })
        )

        await executeRingBuilderSetMutation({
            currency: currency.value,
            locale: locale.value,
            region: regionCode.value,
            input: { mount: {} },
        })
    }

    const stoneToRingBuilderInput = (
        stone: AddStoneTypes
    ): RingBuilderCentreStoneFragmentInput | undefined => {
        if ('__typename' in stone) {
            switch (stone.__typename) {
                case 'GemsApi_Stone':
                    return {
                        gemsApiIdentifier: gemsApiStoneToInput(stone),
                    }
                case 'NonSkuLabGrown':
                    return {
                        nonSkuLabGrownDiamond:
                            nonSkuLabGrownDiamondToFilterInput(stone),
                    }
                case 'NonSkuGemstone':
                    return {
                        nonSkuGemstone: nonSkuLabGemstoneToFilterInput(stone),
                    }
                default:
                    return assertNever(stone, undefined)
            }
        } else {
            switch (stone.type) {
                case 'gemstone':
                    return {
                        nonSkuGemstone: stone.params.nonSkuGemStoneFilter,
                    }
                case 'lab-grown':
                    return {
                        nonSkuLabGrownDiamond:
                            stone.params.nonSkuLabGrownFilter,
                    }
                default:
                    return assertNever(stone, undefined)
            }
        }
    }

    const addStone: RingBuilder['addStone'] = async (s) =>
        executeRingBuilderSetMutation({
            currency: currency.value,
            locale: locale.value,
            region: regionCode.value,
            input: {
                centreStone: stoneToRingBuilderInput(s),
            },
        }).then(() => {
            return stone.value
        })

    const addRingAndStone: RingBuilder['addRingAndStone'] = async (
        r,
        s,
        signatureStone
    ) =>
        executeRingBuilderSetMutation({
            currency: currency.value,
            locale: locale.value,
            region: regionCode.value,
            input: {
                mount: {
                    engagementRing:
                        '__typename' in r
                            ? getCatalogueEngagementRingFilterInput(r)
                            : r,
                    signatureStone,
                },
                centreStone: stoneToRingBuilderInput(s),
            },
        }).then(() => [ring.value, stone.value])

    const removeStone: RingBuilder['removeStone'] = async () => {
        if (ring.value) {
            routerPush(routes.ringBuilder.ring(ring.value, null))
        }

        await executeRingBuilderSetMutation({
            currency: currency.value,
            locale: locale.value,
            region: regionCode.value,
            input: { centreStone: {} },
        })
    }

    const lastAdded = computed((): LastAddedTypename | null => {
        const lastAdded =
            ringBuilder.value?.position2 || ringBuilder.value?.position1

        if (!lastAdded) {
            return null
        }

        switch (lastAdded?.__typename) {
            case 'RingBuilderMountFragment':
                return 'CatalogueEngagementRing'
            case 'RingBuilderCentreStoneFragment':
                return lastAdded.stone?.__typename === 'GemsApiStone'
                    ? 'GemsApi_Stone'
                    : lastAdded.stone?.__typename || null
            default:
                return assertNever(lastAdded, null)
        }
    })

    const isValid = toRef(() => !ringBuilder.value?.failure?.errors.length)

    const klarnaFinanceOptions = toRef(
        () => ringBuilder.value?.financeOptionsKlarna
    )
    const affirmFinanceOptions = toRef(
        () => ringBuilder.value?.financeOptionsAffirm
    )

    // Prices TODO: Why do we need so many of them
    const ringTotalSaleAmount = computed(() =>
        parseFloat(mountTotalSalePrice.value.amount)
    )
    const ringTotalSaleAmountFormatted = computed(() =>
        formatMoney(mountTotalSalePrice.value)
    )

    const totalSaleMoney = computed(
        () =>
            ringBuilder.value?.salePrice.total ||
            ({
                __typename: 'Money',
                amount: '0',
                currency: 'GBP',
            } as const)
    )
    const totalSaleAmount = computed(() =>
        parseFloat(totalSaleMoney.value.amount)
    )
    const totalSaleAmountFormatted = computed(() =>
        totalSaleAmount.value ? formatMoney(totalSaleMoney.value) : ''
    )

    return {
        // Main items
        ring,
        stone,

        // Cookie-persisted store
        stoneUri,
        signatureStone,

        lastAdded,

        isValid,
        fetchingRingBuilderSetMutation,

        // Actions
        addRing,
        removeRing,
        addStone,
        removeStone,
        addRingAndStone,

        // Data Convenience
        productId: toRef(ringBuilder.value?.productId ?? ''),
        image: computed(
            () =>
                ringBuilder.value?.image || mountFragment.value?.ring?.images[0]
        ),
        imageFor3rdStep: computed(() => ringBuilder.value?.image),
        mountFragment,
        centreStoneFragment,

        // Random Strings
        stoneShapeAndWeight,
        stoneDescription,

        // Finance Options
        klarnaFinanceOptions,
        affirmFinanceOptions,

        // Prices TODO: Why do we need so many of them
        ringTotalSaleAmount,
        ringTotalSaleAmountFormatted,

        salePrice: toRef(() => ringBuilder.value?.salePrice),
        totalSale: totalSaleMoney,
        totalSaleAmount,
        totalSaleAmountFormatted,
    }
}

export function useEngagementRingSize(): ReturnType<typeof useRingSize> {
    const { ring } = useRingBuilder()
    const { ringSizePersisted } = useRingBuilderStore()

    return useRingSize(
        toRef(() => ring.value?.sizes),
        ringSizePersisted
    )
}

export function useImageFor3rdStep(): Readonly<
    Ref<
        | {
              small_x2: MonolithImageSizeFragment
              large_x2: MonolithImageSizeFragment
          }
        | undefined
        | null
    >
> {
    const ringBuilder = useRingBuilderDataStore()
    return computed(() => ringBuilder.state.value?.image)
}

export function useStoneShapeAndWeight(): Readonly<Ref<string>> {
    const { stone } = useRingBuilder()
    return computed(
        () => `${getStoneShape(stone.value)} ${getStoneWeight(stone.value)}`
    )
}

export function useIsRingBuilderActive(): ComputedRef<boolean> {
    const router = useRouter()
    const { ring, stone } = useRingBuilder()

    const isERFlow = computed(() => {
        switch (router.currentRoute.value.name) {
            case ROUTE_ENGAGEMENT_RING:
            // fallthrough
            case ROUTE_ENGAGEMENT_RINGS:
            // fallthrough
            case ROUTE_ENGAGEMENT_RING_GEMSTONE:
            // fallthrough
            case ROUTE_ENGAGEMENT_RING_LAB_GROWN:
            // fallthrough
            case ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS:
            // fallthrough
            case ROUTE_NATURAL_DIAMOND_SEARCH:
            // fallthrough
            case ROUTE_LAB_GROWN_DIAMOND_SEARCH:
            // fallthrough
            case ROUTE_FANCY_DIAMOND_SEARCH:
            // fallthrough
            case ROUTE_GEMSTONE_SEARCH:
            // fallthrough
            case ROUTE_NON_SKU_GEMSTONE_SEARCH:
            // fallthrough
            case ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH:
                return true
            default:
                return false
        }
    })

    return computed(
        (): boolean => Boolean(ring.value || stone.value) || isERFlow.value
    )
}

export function useRingBuilderRingShape(): ComputedRef<
    RingBuilderCentreStoneShapeConstraint | undefined
> {
    const { ring } = useRingBuilder()

    return computed(() => ring.value?.centreStoneConstraints?.shape)
}

export function useRingBuilderStoneTypeLabel(): ComputedRef<string> {
    const { stone } = useRingBuilder()

    // TODO Translate
    return computed((): string => {
        const stoneValue = stone.value

        if (!stoneValue) {
            return 'Diamond'
        }

        switch (stoneValue?.__typename) {
            case 'GemsApi_Stone':
                if (!stoneValue.categoryWithInfo.info) {
                    return 'Diamond'
                }

                return getGemsApiCategoryLabel(
                    stoneValue.categoryWithInfo.info?.systemCode
                )
            case 'NonSkuLabGrown':
                return 'Diamond'
            case 'NonSkuGemstone':
                return 'Gemstone'
            default:
                return assertNever(stoneValue, 'Diamond')
        }
    })
}

function getGemsApiCategoryLabel(category: GemsApi_StoneCategory): string {
    // TODO Translate
    switch (category) {
        case 'DIAMOND':
        case 'LAB_GROWN_DIAMOND':
            return 'Diamond'
        case 'GEMSTONE':
            return 'Gemstone'
        case 'FANCY_DIAMOND':
        case 'LAB_GROWN_FANCY_DIAMOND':
            return 'Coloured Diamond'
    }
}
